import React, { useMemo, memo } from 'react';

import styles from './styles.module.css';

type OtpInputProps = {
    value: string;
    inputAmount: number;
    onChange: (e: any) => void;
};

const OtpInput: React.FC<OtpInputProps> = ({ value, inputAmount, onChange }) => {
    /**
     * inputの値を定義するの処理
     */
    const valueItems = useMemo(() => {
        const RE_DIGIT = new RegExp(/^\d+$/);
        const valueArray = value.split('');
        const items = [];

        for (let i = 0; i < inputAmount; i++) {
            const char = valueArray[i];

            if (RE_DIGIT.test(char)) {
                items.push(char);
            } else {
                items.push('');
            }
        }
        return items;
    }, [value, inputAmount]);

    /**
     * 次のinputににフォーカスの処理
     */
    const focusToNextInput = (target: HTMLInputElement) => {
        const nextElementSibling = target.nextElementSibling;

        if (nextElementSibling) {
            (nextElementSibling as HTMLElement).focus();
        }
    };

    /**
     * 前のinputにフォーカスの処理
     */
    const focusToPrevInput = (target: HTMLInputElement) => {
        const previousElementSibling = target.previousElementSibling;

        if (previousElementSibling) {
            (previousElementSibling as HTMLElement).focus();
        }
    };

    /**
     * inputの値が変更するの処理
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const RE_DIGIT = new RegExp(/^\d+$/);
        const target = e.target;
        let targetValue = target.value.trim();
        const isTargetValueDigit = RE_DIGIT.test(targetValue);

        if (!isTargetValueDigit && targetValue !== '') {
            return;
        }

        targetValue = isTargetValueDigit ? targetValue : ' ';

        const targetValueLength = targetValue.length;

        //inputに1桁の数字を入力するの処理
        if (targetValueLength === 1) {
            const newValue =
                value.length > 0
                    ? value.substring(0, idx) + targetValue + value.substring(idx + 1)
                    : value.padStart(idx, ' ') + targetValue + value.padEnd(inputAmount, ' ');

            onChange(newValue);

            if (!isTargetValueDigit) {
                return;
            }

            focusToNextInput(target);
        }
        //inputに6桁の数字を貼り付けるの処理
        else if (targetValueLength === inputAmount) {
            onChange(targetValue);

            target.blur();
        }
    };

    /**
     * インプットキーのイベント処理
     */
    const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = e;
        const target = e.target;

        //右矢印キーまたは下矢印キーが押された場合、次のインプットにフォーカスを移動します。
        if (key === 'ArrowRight' || key === 'ArrowDown') {
            e.preventDefault();
            return focusToNextInput(target as HTMLInputElement);
        }

        //左矢印キーまたは上矢印キーが押された場合、前のインプットにフォーカスを移動します。
        if (key === 'ArrowLeft' || key === 'ArrowUp') {
            e.preventDefault();
            return focusToPrevInput(target as HTMLInputElement);
        }

        const targetValue = (target as HTMLInputElement).value;

        // バックスペースキーが押され、かつインプットが空の場合、前のインプットにフォーカスを移動します。
        if (e.key !== 'Backspace' || targetValue !== '') {
            return;
        }

        focusToPrevInput(target as HTMLInputElement);
    };

    /**
     * インプットフォーカスのイベント処理
     */
    const inputOnClick = (e: React.MouseEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        const endPoint = target.value.length;
        target.focus();
        if (typeof target.setSelectionRange === 'function') {
            target.setSelectionRange(endPoint, endPoint);
        }
    };

    return (
        <div className={styles.otpGroup}>
            {valueItems.map((digit, idx) => (
                <input
                    key={idx}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{1}"
                    maxLength={inputAmount}
                    className={styles.otpInput}
                    value={digit}
                    onChange={(e) => handleInputChange(e, idx)}
                    onKeyDown={inputOnKeyDown}
                    onClick={inputOnClick}
                />
            ))}
        </div>
    );
};

export default memo(OtpInput);
